import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { NoteFormValues, NoteHistoryRecord, NoteRecord } from './types';

const API_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
  Accept: 'application/json',
});

const useController = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [notes, setNotes] = useState<NoteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<NoteHistoryRecord[]>([]);

  const workerId = record?.id;
  const baseUrl = `${API_URL}/admin/candidates/${workerId}/notes`;

  const fetchNotes = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    try {
      const response = await fetch(baseUrl, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch notes');
      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      notify('Error fetching notes', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [workerId, baseUrl, notify]);

  const createNote = useCallback(
    async (values: NoteFormValues) => {
      try {
        const response = await fetch(baseUrl, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(values),
        });
        if (!response.ok) throw new Error('Failed to create note');
        notify('Note created successfully', { type: 'info' });
        await fetchNotes();
      } catch (error) {
        console.error('Error creating note:', error);
        notify('Error creating note', { type: 'warning' });
      }
    },
    [baseUrl, fetchNotes, notify]
  );

  const updateNote = useCallback(
    async (noteId: number, values: NoteFormValues) => {
      try {
        const response = await fetch(`${baseUrl}/${noteId}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(values),
        });
        if (!response.ok) throw new Error('Failed to update note');
        notify('Note updated successfully', { type: 'info' });
        await fetchNotes();
      } catch (error) {
        console.error('Error updating note:', error);
        notify('Error updating note', { type: 'warning' });
      }
    },
    [baseUrl, fetchNotes, notify]
  );

  const deleteNote = useCallback(
    async (noteId: number) => {
      try {
        const response = await fetch(`${baseUrl}/${noteId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to delete note');
        notify('Note deleted successfully', { type: 'info' });
        await fetchNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
        notify('Error deleting note', { type: 'warning' });
      }
    },
    [baseUrl, fetchNotes, notify]
  );

  const fetchHistory = useCallback(
    async (noteId: number) => {
      setHistoryLoading(true);
      try {
        const response = await fetch(`${baseUrl}/${noteId}/history`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch history');
        const data = await response.json();
        setHistory(data);
      } catch (error) {
        console.error('Error fetching history:', error);
        notify('Error fetching note history', { type: 'warning' });
      } finally {
        setHistoryLoading(false);
      }
    },
    [baseUrl, notify]
  );

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return {
    notes,
    loading,
    history,
    historyLoading,
    createNote,
    updateNote,
    deleteNote,
    fetchHistory,
  };
};

export default useController;
